// ABOUTME: In-app password recovery — Kratos recovery (email → code) then a settings flow to set a new password.
// ABOUTME: Recovery grants a privileged session; we extract the settings-flow id and render it inline.

import { useState } from "react"
import { Link, useSearchParams } from "react-router"
import type { RecoveryFlow, SettingsFlow, UpdateRecoveryFlowBody, UpdateSettingsFlowBody } from "@ory/client"
import { useTranslation } from "react-i18next"
import { ory } from "@/model/auth/ory"
import { useKratosFlow } from "@/features/auth/useKratosFlow"
import { FlowForm } from "@/features/auth/FlowForm"
import { AUTH_LINK_CLASS } from "@/features/auth/authStyles"
import { AuthCard } from "./AuthCard"
import { AuthFlowError } from "./AuthFlowError"
import { AuthLoading } from "./AuthLoading"
import { asFlowBody } from "@/features/auth/flowBody"

// Kratos hands recovery off to settings either via continue_with.show_settings_ui (preferred)
// or a redirect_browser_to whose ?flow=… is the settings flow id. Extract, don't follow.
const settingsFlowIdFromSuccess = (data: unknown): string | undefined => {
  const cont = (data as { continue_with?: { action?: string; flow?: { id: string } }[] }).continue_with
  return cont?.find((c) => c.action === "show_settings_ui")?.flow?.id
}
const settingsFlowIdFromRedirect = (url: string): string | undefined => {
  try {
    return new URL(url, window.location.origin).searchParams.get("flow") ?? undefined
  } catch {
    return undefined
  }
}

// After recovery, only the new-password part of the settings flow is relevant.
const passwordOnlySettings = (flow: SettingsFlow): SettingsFlow => ({
  ...flow,
  ui: { ...flow.ui, nodes: flow.ui.nodes.filter((n) => n.group === "password" || n.group === "default") },
})

const SetNewPassword = ({ flowId }: { flowId: string }) => {
  const { t } = useTranslation()
  const { flow, submit, submitting, failed, restart } = useKratosFlow<SettingsFlow>({
    initialFlowId: flowId,
    createFlow: () => ory.getSettingsFlow({ id: flowId }),
    getFlow: (id) => ory.getSettingsFlow({ id }),
    updateFlow: (id, body) =>
      ory.updateSettingsFlow({ flow: id, updateSettingsFlowBody: asFlowBody<UpdateSettingsFlowBody>(body) }),
    transformFlow: passwordOnlySettings,
  })

  if (failed)
    return (
      <AuthFlowError
        title={t("auth.password")}
        onRetry={restart}
      />
    )
  if (!flow) return <AuthLoading />
  return (
    <AuthCard title={t("auth.password")}>
      <FlowForm
        flow={flow}
        submitting={submitting}
        onSubmit={submit}
      />
    </AuthCard>
  )
}

const RecoveryStep = ({ onSettingsFlow }: { onSettingsFlow: (id: string) => void }) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get("return_to")

  const { flow, submit, submitting, failed, restart } = useKratosFlow<RecoveryFlow>({
    createFlow: () => ory.createBrowserRecoveryFlow(),
    getFlow: (id) => ory.getRecoveryFlow({ id }),
    updateFlow: (id, body) =>
      ory.updateRecoveryFlow({ flow: id, updateRecoveryFlowBody: asFlowBody<UpdateRecoveryFlowBody>(body) }),
    stayOnSuccess: true, // email step → re-render with the code field
    onSuccess: (data) => {
      const id = settingsFlowIdFromSuccess(data)
      if (id) {
        onSettingsFlow(id)
        return true
      }
      return false
    },
    onRedirect: (url) => {
      const id = settingsFlowIdFromRedirect(url)
      if (id) {
        onSettingsFlow(id)
        return true
      }
      return false
    },
  })

  if (failed)
    return (
      <AuthFlowError
        title={t("auth.forgotPassword")}
        onRetry={restart}
      />
    )
  if (!flow) return <AuthLoading />
  return (
    <AuthCard
      title={t("auth.forgotPassword")}
      footer={
        <Link
          to={returnTo ? `/auth/login?return_to=${encodeURIComponent(returnTo)}` : "/auth/login"}
          className={AUTH_LINK_CLASS}
        >
          {t("auth.backToLogin")}
        </Link>
      }
    >
      <FlowForm
        flow={flow}
        submitting={submitting}
        onSubmit={submit}
      />
    </AuthCard>
  )
}

export const RecoveryPage = () => {
  const [settingsFlowId, setSettingsFlowId] = useState<string | null>(null)
  return settingsFlowId ? (
    <SetNewPassword flowId={settingsFlowId} />
  ) : (
    <RecoveryStep onSettingsFlow={setSettingsFlowId} />
  )
}
